/**
 * HealthCards — the run's health at its latest step: gradient norm,
 * context pressure, dynamic sampling and, for a grpo run, the rollout
 * attempts that errored or were cancelled (TRA-1377). Each card renders when the run
 * measures its data, so an SFT run (no sampling) shows two and a run that
 * has not reached its first step shows none.
 */

import type {
  TrainingRolloutErrors,
  TrainingRunDocument,
  TrainingSampling,
  TrainingSeries,
  TrainingSftTokenUsage,
  TrainingTokenUsage,
} from '@/api/generated/model'
import { CHART_INK } from '@/components/evals/chartStyle'
import { Badge } from '@/components/ui/badge'

/** One muted polyline at card width, domain [0, max × 1.1]. */
function Sparkline({ series }: { series: number[] }): React.JSX.Element {
  const height = 36
  const width = 100
  // A one-point series (a run's first exported step) still paints: a
  // single-vertex polyline strokes nothing, so widen the point into a
  // horizontal segment.
  const drawn = series.length === 1 ? [...series, ...series] : series
  const top = Math.max(...drawn, 0) * 1.1 || 1
  const y = (value: number): number => height - (value / top) * (height - 4) - 2
  const points = drawn
    .map((value, index) => {
      const x = drawn.length > 1 ? (index / (drawn.length - 1)) * width : 0
      return `${x.toFixed(1)},${y(value).toFixed(1)}`
    })
    .join(' ')
  return (
    <svg
      viewBox={`0 0 ${width.toString()} ${height.toString()}`}
      preserveAspectRatio="none"
      className="h-12 w-full"
      aria-hidden
    >
      <polyline
        points={points}
        fill="none"
        stroke={CHART_INK}
        strokeWidth={1.25}
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  )
}

export function CardLabel({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
      {children}
    </p>
  )
}

function GradientNormCard({ gradNorm }: { gradNorm: TrainingSeries }): React.JSX.Element {
  return (
    <div className="border border-border p-5">
      <CardLabel>Gradient norm</CardLabel>
      <p className="mt-4 flex items-baseline gap-2">
        <span className="font-mono text-2xl tabular-nums">{gradNorm.value.toFixed(2)}</span>
        <span className="text-xs text-muted-foreground">latest step</span>
      </p>
      <div className="mt-4">
        <Sparkline series={gradNorm.series} />
      </div>
      <p className="mt-4 text-xs leading-relaxed text-muted-foreground">
        The optimizer clips updates when this spikes, so a flat line means stable steps.
      </p>
    </div>
  )
}

interface ContextSegment {
  label: string
  tokens: number
  swatch: string
}

/** A reward run splits each sample into prompt and generation; an SFT
 * demonstration is one sequence. */
function usedSegments(tokens: TrainingTokenUsage | TrainingSftTokenUsage): ContextSegment[] {
  if ('prompt_tokens_mean' in tokens) {
    return [
      { label: 'Prompt + tools', tokens: tokens.prompt_tokens_mean, swatch: 'bg-brand-gunmetal-blue' },
      { label: 'Generation', tokens: tokens.generation_tokens_mean, swatch: 'bg-brand-neutral-olive' },
    ]
  }
  return [{ label: 'Demonstration', tokens: tokens.total_tokens_mean, swatch: 'bg-brand-gunmetal-blue' }]
}

function SegmentList({ segments }: { segments: ContextSegment[] }): React.JSX.Element {
  return (
    <dl className="mt-4 space-y-2">
      {segments.map((segment) => (
        <div key={segment.label} className="flex items-center justify-between gap-3 text-xs">
          <dt className="flex items-center gap-2 text-muted-foreground">
            <span className={`h-[9px] w-[9px] rounded-[1.5px] ${segment.swatch}`} />
            {segment.label}
          </dt>
          <dd className="m-0 font-mono tabular-nums">{(segment.tokens / 1000).toFixed(1)}k</dd>
        </div>
      ))}
    </dl>
  )
}

function ContextCard({
  tokens,
}: {
  tokens: TrainingTokenUsage | TrainingSftTokenUsage
}): React.JSX.Element {
  const used = usedSegments(tokens)
  const usedTokens = used.reduce((sum, segment) => sum + segment.tokens, 0)
  const usedShare = Math.round((usedTokens / tokens.context_window) * 100)
  const free = Math.max(tokens.context_window - usedTokens, 0)
  const segments = [...used, { label: 'Free', tokens: free, swatch: 'bg-border/40' }]
  return (
    <div className="border border-border p-5">
      <div className="flex items-center justify-between gap-3">
        <CardLabel>Context pressure</CardLabel>
        <Badge variant="secondary">{usedShare >= 60 ? 'Watch' : 'Comfortable'}</Badge>
      </div>
      <p className="mt-4 flex items-baseline gap-2">
        <span className="font-mono text-2xl tabular-nums">{usedShare}%</span>
        <span className="text-xs text-muted-foreground">
          of {Math.round(tokens.context_window / 1000).toString()}k window
        </span>
      </p>
      <div className="mt-4 flex h-2 w-full overflow-hidden rounded-[1.5px]">
        {segments.map((segment) => (
          <div
            key={segment.label}
            className={segment.swatch}
            style={{ width: `${((segment.tokens / tokens.context_window) * 100).toString()}%` }}
          />
        ))}
      </div>
      <SegmentList segments={segments} />
      <p className="mt-4 text-xs leading-relaxed text-muted-foreground">
        The bar shows mean tokens per sample at the latest step. The longest sample reached{' '}
        {(tokens.total_tokens_max / 1000).toFixed(1)}k tokens.
      </p>
    </div>
  )
}

function SamplingCard({ sampling }: { sampling: TrainingSampling }): React.JSX.Element {
  return (
    <div className="border border-border p-5">
      <CardLabel>Dynamic sampling</CardLabel>
      <p className="mt-4 flex items-baseline gap-2">
        <span className="font-mono text-2xl tabular-nums">{sampling.refill_latest.toFixed(0)}</span>
        <span className="text-xs text-muted-foreground">
          of {sampling.refill_limit.toString()} refill batches used
        </span>
      </p>
      <div className="mt-4">
        <Sparkline series={sampling.dropped_series} />
      </div>
      <p className="mt-4 text-xs leading-relaxed text-muted-foreground">
        The sampler drops a group when one of its rollouts is ineligible for training (it errored
        or failed the quality check) and refills from other prompts. It has dropped{' '}
        {sampling.groups_dropped_total.toLocaleString()} groups so far.
      </p>
    </div>
  )
}

function RolloutErrorsCard({ errors }: { errors: TrainingRolloutErrors }): React.JSX.Element {
  const kinds = Object.entries(errors.by_type).sort(([, a], [, b]) => b - a)
  return (
    <div className="border border-border p-5">
      <CardLabel>Rollout errors</CardLabel>
      <p className="mt-4 flex items-baseline gap-2">
        <span className="font-mono text-2xl tabular-nums">{errors.errored.toLocaleString()}</span>
        <span className="text-xs text-muted-foreground">
          of {errors.attempts.toLocaleString()} attempts ended in an error
        </span>
      </p>
      {kinds.length > 0 && (
        <ul className="mt-4 space-y-1">
          {kinds.map(([kind, count]) => (
            <li key={kind} className="flex justify-between font-mono text-xs">
              <span>{kind}</span>
              <span className="tabular-nums">{count.toLocaleString()}</span>
            </li>
          ))}
        </ul>
      )}
      <p className="mt-4 text-xs leading-relaxed text-muted-foreground">
        An error is a failure such as a sandbox crash or an agent timeout. A retried rollout
        counts once per attempt. {errors.cancelled.toLocaleString()} more attempts were cancelled,
        which is expected at the end of each step and when a run stops.
      </p>
    </div>
  )
}

export function HealthCards({ run }: { run: TrainingRunDocument }): React.JSX.Element | null {
  const errors = run.rollout_errors ?? null
  if (run.grad_norm === null && run.tokens === null && run.sampling === null && errors === null) {
    return null
  }
  return (
    <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
      {run.grad_norm !== null && <GradientNormCard gradNorm={run.grad_norm} />}
      {run.tokens !== null && <ContextCard tokens={run.tokens} />}
      {run.sampling !== null && <SamplingCard sampling={run.sampling} />}
      {errors !== null && <RolloutErrorsCard errors={errors} />}
    </div>
  )
}
